class MinerwaProduct extends HTMLElement {
  connectedCallback() {
    this.frames = [...this.querySelectorAll('[data-frame]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.form = this.querySelector('[data-add-form]');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.addButton = this.querySelector('[data-add-button]');
    this.addLabel = this.querySelector('[data-add-label]');
    this.addError = this.querySelector('[data-add-error]');

    try {
      this.variants = JSON.parse(this.querySelector('[data-variant-map]').textContent);
    } catch {
      this.variants = [];
    }

    this.selected = this.variants.find((v) => String(v.id) === this.variantInput?.value) || this.variants[0];

    this.bindThumbs();
    this.bindOptions();
    this.bindLightbox();
    this.bindForm();
    this.bindVideos();
  }

  disconnectedCallback() {
    this.spy?.disconnect();
    this.videoObserver?.disconnect();
    document.removeEventListener('keydown', this.onKeyDown);
  }

  bindThumbs() {
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number(thumb.dataset.thumb);
        this.frames[index]?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        this.setActiveThumb(index);
      });
    });

    this.spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            this.setActiveThumb(Number(entry.target.dataset.frame));
          }
        });
      },
      { threshold: 0.5 },
    );
    this.frames.forEach((frame) => this.spy.observe(frame));
  }

  setActiveThumb(index) {
    this.thumbs.forEach((t, i) => t.classList.toggle('minerwa-pdp__thumb--active', i === index));
  }

  bindVideos() {
    const videos = [...this.querySelectorAll('[data-pdp-video]')];
    if (videos.length === 0) return;
    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (video.preload !== 'auto') { video.preload = 'auto'; video.load(); }
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.4 },
    );
    videos.forEach((v) => this.videoObserver.observe(v));
  }

  bindOptions() {
    const swatches = [...this.querySelectorAll('[data-option-value-name]')];
    if (swatches.length === 0) return;

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const index = Number(swatch.dataset.optionIndex);
        const chosen = this.currentOptions();
        chosen[index] = swatch.dataset.optionValueName;

        const match = this.variants.find((v) => v.options.every((opt, i) => opt === chosen[i]));
        if (!match) return;

        swatches
          .filter((s) => Number(s.dataset.optionIndex) === index)
          .forEach((s) => s.classList.toggle('minerwa-pdp__swatch--active', s === swatch));

        const readout = this.querySelector(`[data-option-value="${index}"]`);
        if (readout) readout.textContent = swatch.dataset.optionValueName;

        this.applyVariant(match);
      });
    });
  }

  currentOptions() {
    return [...this.querySelectorAll('.minerwa-pdp__swatch--active')]
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map((s) => s.dataset.optionValueName);
  }

  applyVariant(variant) {
    this.selected = variant;
    if (this.variantInput) this.variantInput.value = variant.id;

    const price = this.querySelector('[data-price]');
    const compare = this.querySelector('[data-compare]');
    if (price) price.textContent = this.money(variant.price);
    if (compare) {
      if (variant.compare_at > variant.price) {
        compare.textContent = this.money(variant.compare_at);
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    }

    const sku = this.querySelector('[data-sku]');
    if (sku && variant.sku) sku.textContent = variant.sku;

    if (this.addButton) {
      this.addButton.disabled = !variant.available;
      if (this.addLabel) this.addLabel.textContent = variant.available ? this.addLabel.dataset.add || this.addLabel.textContent : 'Sold Out';
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  money(cents) {
    const rupees = Math.round(cents / 100);
    return `Rs. ${rupees.toLocaleString('en-IN')}`;
  }

  bindForm() {
    if (!this.form) return;
    this.form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!this.selected || this.addButton?.disabled) return;

      const original = this.addLabel?.textContent;
      this.clearError();
      if (this.addLabel) this.addLabel.textContent = 'Adding…';
      this.addButton.disabled = true;

      try {
        await this.addToCart();
        if (this.addLabel) this.addLabel.textContent = 'Added';
        setTimeout(() => {
          if (this.addLabel) this.addLabel.textContent = original;
          this.addButton.disabled = false;
        }, 1600);
      } catch (error) {
        if (this.addLabel) this.addLabel.textContent = original;
        this.addButton.disabled = false;
        this.showError(error?.message);
      }
    });
  }

  collectProperties() {
    const properties = {};

    this.form?.querySelectorAll('[name^="properties["]').forEach((input) => {
      if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;

      const key = input.name.slice(11, -1);
      const value = typeof input.value === 'string' ? input.value.trim() : input.value;
      if (key && value) properties[key] = value;
    });

    return properties;
  }

  async addToCart() {
    const { CartLinesUpdateEvent } = await import('@shopify/events');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const properties = this.collectProperties();
    const hasProperties = Object.keys(properties).length > 0;

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(this.selected.id), quantity: 1 }],
        promise: deferred.promise,
      }),
    );

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            hasProperties
              ? { id: Number(this.selected.id), quantity: 1, properties }
              : { id: Number(this.selected.id), quantity: 1 },
          ],
          sections: sectionIds.join(','),
        }),
      });

      const added = await response.json();
      if (!response.ok || added.status) {
        throw new Error(added.description || added.message || 'This item could not be added to your bag.');
      }

      const cart = await fetch('/cart.js').then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'minerwa-product',
          itemCount: 1,
          sections: added.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }

  showError(message) {
    if (!this.addError) return;
    this.addError.textContent = message || 'This item could not be added to your bag.';
    this.addError.hidden = false;
  }

  clearError() {
    if (!this.addError) return;
    this.addError.hidden = true;
    this.addError.textContent = '';
  }

  bindLightbox() {
    this.lightbox = this.querySelector('[data-lightbox]');
    this.stage = this.querySelector('[data-lightbox-stage]');
    if (!this.lightbox) return;

    this.frames.forEach((frame, index) => {
      frame.addEventListener('click', () => this.openLightbox(index));
    });

    this.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.closeLightbox());
    this.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.stepLightbox(-1));
    this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.stepLightbox(1));
    this.lightbox.addEventListener('click', (event) => {
      if (event.target === this.lightbox) this.closeLightbox();
    });

    this.onKeyDown = (event) => {
      if (this.lightbox.hidden) return;
      if (event.key === 'Escape') this.closeLightbox();
      if (event.key === 'ArrowLeft') this.stepLightbox(-1);
      if (event.key === 'ArrowRight') this.stepLightbox(1);
    };
    document.addEventListener('keydown', this.onKeyDown);
  }

  openLightbox(index) {
    this.lightboxIndex = index;
    this.renderLightbox();
    this.lightbox.hidden = false;
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    this.lightbox.hidden = true;
    this.stage.replaceChildren();
    document.body.style.overflow = this.previousOverflow ?? '';
  }

  stepLightbox(direction) {
    const total = this.frames.length;
    this.lightboxIndex = (this.lightboxIndex + direction + total) % total;
    this.renderLightbox();
  }

  renderLightbox() {
    const source = this.frames[this.lightboxIndex]?.querySelector('img, video');
    if (!source) return;
    const clone = source.cloneNode(true);
    clone.removeAttribute('loading');
    if (clone.tagName === 'VIDEO') {
      clone.controls = true;
      clone.muted = false;
      clone.preload = 'auto';
    }
    this.stage.replaceChildren(clone);
  }
}

if (!customElements.get('minerwa-product')) {
  customElements.define('minerwa-product', MinerwaProduct);
}
